import {
  EntityType,
  ExactMatchQueryOperators,
  FileQueryOperators,
  MatchQueryOperators,
  QueryOperator,
  VqlIssuesValues,
  VqlMatchOps,
  VqlModeOps,
  VqlStrOps,
  VqlQuery,
  VqlQueryBase,
  FilterType,
  FilterKey,
  CustomMetadataValue,
  VqlCustomMetadataOps,
  CustomMetadataOperators,
  CustomMetadata,
  Filters,
  SizeFilterOp,
  APIQueryOperators,
  SizeSupportedOperators,
  FilterMenuItemType,
  DateTimeFilterOp,
} from "types";
import { v4 as uuidv4 } from "uuid";
import {
  LABELS_PARAMETER,
  USER_TAGS_PARAMETER,
  VQL_PARAMETER,
} from "helpers/constants/miscellaneous";
import { isCustomMetadata } from "./customMetadataHelpers";

const matchQueryOperatorToAPIQueryOperators: Record<
  MatchQueryOperators,
  VqlMatchOps
> = {
  [QueryOperator.IS]: "is",
  [QueryOperator.IS_NOT]: "is_not",
  [QueryOperator.IS_ONE_OF]: "one_of",
  [QueryOperator.IS_NOT_ONE_OF]: "not_one_of",
};

const exactMatchQueryOperatorToAPIQueryModes: Record<
  ExactMatchQueryOperators,
  VqlModeOps
> = {
  [QueryOperator.IS]: "in",
  [QueryOperator.IS_NOT]: "out",
};

const fileQueryOperatorToAPIQueryOperators: Record<
  FileQueryOperators,
  VqlStrOps | "one_of"
> = {
  [QueryOperator.IS_ONE_OF]: "one_of",
  [QueryOperator.CONTAINS]: "contains",
  [QueryOperator.STARTS_WITH]: "starts_with",
  [QueryOperator.ENDS_WITH]: "ends_with",
};

export const customMetadataOperatorToAPIQueryOperators: Record<
  CustomMetadataOperators,
  VqlCustomMetadataOps
> = {
  [QueryOperator.IS]: "is",
  [QueryOperator.IS_NOT]: "is_not",
  [QueryOperator.IS_ONE_OF]: "one_of",
  [QueryOperator.IS_NOT_ONE_OF]: "not_one_of",
  [QueryOperator.CONTAINS]: "contains",
  [QueryOperator.GREATER_THAN]: "gt",
  [QueryOperator.LESS_THAN]: "lt",
  [QueryOperator.GREATER_THAN_OR_EQUAL_TO]: "gte",
  [QueryOperator.LESS_THAN_OR_EQUAL_TO]: "lte",
  [QueryOperator.BETWEEN]: "between",
  [QueryOperator.EQUAL]: "eq",
};

const getApiQueryOperatorByQueryOperators: Record<
  QueryOperator,
  APIQueryOperators
> = {
  [QueryOperator.IS]: "is",
  [QueryOperator.IS_NOT]: "is_not",
  [QueryOperator.IS_ONE_OF]: "one_of",
  [QueryOperator.IS_NOT_ONE_OF]: "not_one_of",
  [QueryOperator.CONTAINS]: "contains",
  [QueryOperator.GREATER_THAN]: "gt",
  [QueryOperator.LESS_THAN]: "lt",
  [QueryOperator.GREATER_THAN_OR_EQUAL_TO]: "gte",
  [QueryOperator.LESS_THAN_OR_EQUAL_TO]: "lte",
  [QueryOperator.BETWEEN]: "between",
  [QueryOperator.ENDS_WITH]: "ends_with",
  [QueryOperator.STARTS_WITH]: "starts_with",
  [QueryOperator.DOES_NOT_CONTAIN]: "does_not_contain",
  [QueryOperator.EQUAL]: "eq",
};

export const createAndUpdateLabelsFilter = (
  labels: string[],
  operator: MatchQueryOperators,
  id?: string,
): VqlQuery => ({
  id: id || uuidv4(),
  labels: {
    op: matchQueryOperatorToAPIQueryOperators[operator] as VqlMatchOps,
    value: labels,
  },
});

export const createAndUpdateMediaStatusFilter = (
  mediaStatusValue: string,
  operator: MatchQueryOperators,
  id?: string,
): VqlQuery => ({
  id: id || uuidv4(),
  media_status: {
    op: "is",
    value: mediaStatusValue,
  },
});

export const createAndUpdateIssueTypesFilter = (
  issueType: Omit<VqlIssuesValues, "duplicates">,
  operator: ExactMatchQueryOperators = QueryOperator.IS,
  confidenceRange: [number, number] = [0, 1],
  id?: string,
): VqlQuery => ({
  id: id || uuidv4(),
  issues: {
    op: "issue",
    value: issueType.toLowerCase() as VqlIssuesValues,
    confidence_min: confidenceRange[0],
    confidence_max: confidenceRange[1],
    mode: exactMatchQueryOperatorToAPIQueryModes[operator] as VqlModeOps,
  },
});

export const createAndUpdateDuplicatesFilter = (
  threshold: number,
  id?: string,
): VqlQuery => ({
  id: id || uuidv4(),
  duplicates: {
    op: "duplicates",
    value: threshold,
  },
});

export const createAndUpdateSizeFilter = (
  size: number,
  operator: SizeSupportedOperators,
  id?: string,
): VqlQuery => ({
  id: id || uuidv4(),
  size: {
    op: getApiQueryOperatorByQueryOperators[
      operator as CustomMetadataOperators
    ] as SizeFilterOp,
    value: size,
  },
});

export const createAndUpdateDateFilter = (
  value: string | [string, string],
  operator: DateTimeFilterOp,
  id?: string,
): VqlQuery => ({
  id: id ?? uuidv4(),
  created_at: {
    op: getApiQueryOperatorByQueryOperators[
      operator as CustomMetadataOperators
    ] as DateTimeFilterOp,
    value: value,
  },
});

export const createAndUpdateUniquenessFilter = (
  threshold: number,
  id?: string,
): VqlQuery => ({
  id: id || uuidv4(),
  uniqueness: { op: "uniqueness", value: threshold },
});

export const createAndUpdateUserTagsFilter = (
  userTags: string[],
  operator: MatchQueryOperators,
  id?: string,
): VqlQuery => ({
  id: id || uuidv4(),
  tags: {
    op: matchQueryOperatorToAPIQueryOperators[operator] as VqlMatchOps,
    value: userTags,
  },
});

export const createAndUpdateSearchTextFilter = (
  searchText: string,
  id?: string,
  negation?: boolean,
  existingQuery?: VqlQuery,
): VqlQuery => {
  const existingText =
    existingQuery && "text" in existingQuery ? existingQuery.text : null;
  const preservedNegation =
    negation !== undefined ? negation : existingText?.negation;
  const preservedGroupId = existingText?.["groupId"];

  return {
    id: id || uuidv4(),
    text: {
      op: "semantic",
      value: searchText,
      ...(preservedNegation !== undefined && { negation: preservedNegation }),
      ...(preservedGroupId && { groupId: preservedGroupId }),
    },
  };
};

export const createAndUpdateCaptionFilter = (
  caption: string,
  id?: string,
): VqlQuery => ({
  id: id || uuidv4(),
  text: {
    op: "fts",
    value: caption,
  },
});

type BoundingBox = [number, number, number, number];

export const createAndUpdateVisualSimilarityFilter = (
  referenceImageId: string,
  boundingBox?: BoundingBox,
  id?: string,
  operation: "media" | "upload" = "media",
  negation?: boolean,
  existingQuery?: VqlQuery,
): VqlQuery => {
  // When editing an existing query, preserve its groupId and negation unless explicitly overridden
  const existingSimilarity =
    existingQuery && "similarity" in existingQuery
      ? existingQuery.similarity
      : null;
  const preservedNegation =
    negation !== undefined ? negation : existingSimilarity?.negation;
  const preservedGroupId = existingSimilarity?.["groupId"];

  return {
    id: id || uuidv4(),
    similarity: {
      op: operation,
      value: referenceImageId,
      ...(boundingBox && {
        bounding_box: boundingBox.map((v) => Math.round(v)) as BoundingBox,
      }),
      ...(preservedNegation !== undefined && { negation: preservedNegation }),
      ...(preservedGroupId && { groupId: preservedGroupId }),
    },
  };
};

export const createAndUpdateEntityTypeFilter = (
  entityType: EntityType,
  id?: string,
): VqlQuery => ({
  id: id || uuidv4(),
  entity_type: {
    op: "is",
    value: entityType,
  },
});

export const createAndUpdateCustomMetadataFilter = (
  operator: CustomMetadataOperators,
  value: CustomMetadataValue,
  field: CustomMetadata,
  id?: string,
): VqlQuery => {
  const finalValue = Array.isArray(value) ? [...value] : value;

  const query = {
    id: id || uuidv4(),
    custom_metadata: {
      ...field,
      op: customMetadataOperatorToAPIQueryOperators[operator],
      value: finalValue,
    },
  };
  return query;
};

export const createAndUpdateFileFilter = (
  file: string | string[],
  operator: FileQueryOperators,
  id?: string,
): VqlQuery => ({
  id: id || uuidv4(),
  file: {
    op: fileQueryOperatorToAPIQueryOperators[operator] as
      | VqlMatchOps
      | VqlStrOps,
    value: file,
  },
});

export const createAndUpdateFolderFilter = (
  folder: string | string[],
  operator: FileQueryOperators,
  id?: string,
): VqlQuery => ({
  id: id || uuidv4(),
  folder: {
    op: fileQueryOperatorToAPIQueryOperators[operator] as
      | VqlMatchOps
      | VqlStrOps,
    value: folder,
  },
});

export function extractValuesFromVqlQueries(
  vqlQueries: VqlQuery[],
): Record<string, string> {
  const extractedValues: Record<string, string> = {};

  vqlQueries.forEach((query) => {
    // Extract labels with "is" operator - only if there are actual values
    if (
      "labels" in query &&
      query.labels &&
      query.labels.op === "is" &&
      query.labels.value.length > 0
    ) {
      extractedValues[LABELS_PARAMETER] = JSON.stringify(query.labels.value);
    }

    // Extract tags with "is" operator - only if there are actual values
    if (
      "tags" in query &&
      query.tags &&
      query.tags.op === "is" &&
      query.tags.value.length > 0
    ) {
      extractedValues[USER_TAGS_PARAMETER] = JSON.stringify(query.tags.value);
    }
  });

  return extractedValues;
}

export const isVqlQuery = <K extends FilterKey>(
  query: VqlQuery,
  key: K,
): query is VqlQueryBase & Extract<FilterType, Record<K, any>> => {
  return key in query;
};

export const getQueryValuesForFilter = <K extends FilterKey>(
  editQueryId: string | undefined,
  oldQuery: VqlQuery | null,
  selectedOperator: QueryOperator,
  queryOperator: QueryOperator,
  existingQuery: VqlQuery | null,
  key: K,
): string[] => {
  // Case 1: Editing & operator unchanged → use old query values
  if (editQueryId && oldQuery && selectedOperator === queryOperator) {
    return ((oldQuery as any)[key]?.value as string[]) || [];
  }

  // Case 2: Creating new & existing query matches → use existing query values
  if (!editQueryId && existingQuery && isVqlQuery(existingQuery, key)) {
    return ((existingQuery as any)[key]?.value as string[]) || [];
  }

  // Case 3: Editing & operator changed → fallback to existing query values
  if (editQueryId && existingQuery && isVqlQuery(existingQuery, key)) {
    return ((existingQuery as any)[key]?.value as string[]) || [];
  }

  // Default
  return [];
};

export const getExistingCustomMetadataQuery = (
  vqlQueries: VqlQuery[],
  fieldId: string,
  operator: CustomMetadataOperators,
): VqlQuery | null => {
  return (
    vqlQueries.find(
      (query: VqlQuery) =>
        isVqlQuery(query, "custom_metadata") &&
        query.custom_metadata?.field_id === fieldId &&
        query.custom_metadata?.op ===
          customMetadataOperatorToAPIQueryOperators[operator],
    ) || null
  );
};

export const getCustomMetadataQueryValue = (
  editQueryId: string | undefined,
  oldQuery: VqlQuery | null,
  selectedOperator: QueryOperator,
  defaultOperator: QueryOperator | null,
  existingQuery: VqlQuery | null,
): CustomMetadataValue => {
  // Case 1: Editing & operator unchanged → use old query values
  if (
    editQueryId &&
    oldQuery &&
    selectedOperator === defaultOperator &&
    isVqlQuery(oldQuery, "custom_metadata")
  ) {
    return oldQuery.custom_metadata?.value || [];
  }

  // Case 2: Creating new & existing query matches → use existing query values
  if (
    !editQueryId &&
    existingQuery &&
    isVqlQuery(existingQuery, "custom_metadata")
  ) {
    return existingQuery.custom_metadata?.value || [];
  }

  // Case 3: Editing & operator changed → fallback to existing query values
  if (
    editQueryId &&
    existingQuery &&
    isVqlQuery(existingQuery, "custom_metadata")
  ) {
    return existingQuery.custom_metadata?.value || [];
  }

  // Default
  return [];
};

const queryMatchersByFilter: Record<Filters, (q: VqlQuery) => boolean> = {
  [Filters.LABELS]: (q) => "labels" in q,
  [Filters.USER_TAGS]: (q) => "tags" in q,
  [Filters.CAPTION]: (q) => "text" in q,
  [Filters.DUPLICATES]: (q) => "duplicates" in q,
  [Filters.FILE]: (q) => "file" in q,
  [Filters.FOLDER]: (q) => "folder" in q,
  [Filters.UNIQUENESS]: (q) => "uniqueness" in q,
  [Filters.ISSUES]: () => false,
  [Filters.MISLABELS]: (q) => "issues" in q && q.issues?.value === "mislabels",
  [Filters.OUTLIERS]: (q) => "issues" in q && q.issues?.value === "outliers",
  [Filters.LABEL_OUTLIER]: (q) =>
    "issues" in q && q.issues?.value === "label_outlier",
  [Filters.SIZE]: (q) => "size" in q,
  [Filters.CREATED_AT]: (q) => "created_at" in q,
  [Filters.MEDIA_STATUS]: (q) => "media_status" in q,
  [Filters.SEMANTIC]: (q) => "text" in q && q.text?.op === "semantic",
  [Filters.VISUAL_SIMILARITY]: (q) => "similarity" in q,
};

export const getFilterKeyByFilterType = (
  filterType: FilterMenuItemType,
): FilterKey | null => {
  if (isCustomMetadata(filterType)) {
    return filterType.field_name as FilterKey;
  }

  const staticMap: Record<Filters, FilterKey> = {
    [Filters.FILE]: "file",
    [Filters.FOLDER]: "folder",
    [Filters.LABELS]: "labels",
    [Filters.USER_TAGS]: "tags",
    [Filters.CAPTION]: "text",
    [Filters.MISLABELS]: "issues",
    [Filters.LABEL_OUTLIER]: "issues",
    [Filters.DUPLICATES]: "duplicates",
    [Filters.OUTLIERS]: "issues",
    [Filters.ISSUES]: "issues",
    [Filters.UNIQUENESS]: "uniqueness",
    [Filters.SIZE]: "size",
    [Filters.CREATED_AT]: "created_at",
    [Filters.MEDIA_STATUS]: "media_status",
    [Filters.SEMANTIC]: "text",
    [Filters.VISUAL_SIMILARITY]: "similarity",
  };

  return staticMap[filterType as Filters] || null;
};

export const getExistingQueryByFilterType = (
  type: Filters,
  queries: VqlQuery[],
): VqlQuery | undefined => {
  const matchedQuery = queryMatchersByFilter[type];
  return queries.find(matchedQuery);
};

const removeFilterFromVqlQuery = (
  searchParams: URLSearchParams,
  filterKey: FilterKey,
) => {
  const vqlParam = searchParams.get(VQL_PARAMETER);
  if (vqlParam) {
    const decoded = decodeURIComponent(vqlParam);
    const filters = JSON.parse(decoded);

    const filtered = filters.filter((filter: any) => !(filterKey in filter));

    if (filtered.length === 0) {
      searchParams.delete(VQL_PARAMETER);
    } else {
      searchParams.set(VQL_PARAMETER, JSON.stringify(filtered));
    }
  }
};

export const removeFiltersFromVqlQuery = (
  searchParams: URLSearchParams,
  filterKeys: FilterKey[],
) => {
  filterKeys.forEach((key) => {
    removeFilterFromVqlQuery(searchParams, key);
  });
};
